import React from 'react';
import { motion } from 'framer-motion';
import { fadeInUp, staggerItem, cardVariants } from '../utils/animations';
import { useTheme, getThemeColors } from '../contexts/ThemeContext';
import Section from './Section';
import Card from './Card';
import Button from './Button';

const Capabilities = () => {
  const { isDarkMode } = useTheme();
  const colors = getThemeColors(isDarkMode);
  
  const capabilities = [
    {
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      title: "Hot Rolling",
      description: "High-temperature steel forming with precise temperature control for optimal mechanical properties and surface finish.",
      features: ["1200°C rolling temperature", "Automated controls", "Uniform heating", "Optimal grain structure"],
      delay: 0
    },
    {
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M19 4h1a1 1 0 011 1v1M5 4H4a1 1 0 00-1 1v1m0 4v6m20-6v6m0 4v1a1 1 0 01-1 1h-1m-6 0H9m-4 0H4a1 1 0 01-1-1v-1" />
        </svg>
      ),
      title: "Cold Shearing",
      description: "Precision cutting and finishing operations for exact length specifications and clean edge profiles.",
      features: ["±2mm length tolerance", "Clean cut edges", "Automated sizing", "Multiple length options"],
      delay: 0.1
    },
    {
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      ),
      title: "Billet Processing",
      description: "Complete billet preparation and conditioning including heating, scaling removal, and quality inspection.",
      features: ["Grade verification", "Defect detection", "Optimal heating curves", "Surface preparation"],
      delay: 0.2
    },
    {
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: "Quality Laboratory",
      description: "Advanced testing facilities for chemical analysis, mechanical testing, and dimensional verification.",
      features: ["Tensile testing", "Chemical analysis", "Hardness testing", "Metallographic examination"],
      delay: 0.3
    },
    {
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      title: "Custom Grades",
      description: "Specialized steel chemistries and properties tailored to specific application requirements and industry standards.",
      features: ["ASTM A615 compliant", "Custom chemistries", "Application-specific", "Technical consultation"],
      delay: 0.4
    },
    {
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
      ),
      title: "Logistics Support",
      description: "Comprehensive supply chain management with flexible delivery options and inventory planning services.",
      features: ["48-72 hour delivery", "Nationwide coverage", "Inventory management", "Just-in-time delivery"],
      delay: 0.5
    }
  ];

  return (
    <Section id="capabilities" background="gradient" className="relative">
      {/* Section Header */}
      <motion.div
        className="text-center mb-16"
        variants={fadeInUp}
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
      >
        <h2 className={`text-h2 font-bold ${colors.text.primary} mb-6`}>
          Our <span className="text-primary-400">Capabilities</span>
        </h2>
        <p className={`text-body-lg ${colors.text.secondary} max-w-3xl mx-auto leading-relaxed`}>
          From raw billet processing to finished product delivery, our integrated capabilities 
          ensure consistent quality and reliable supply for your steel requirements.
        </p>
      </motion.div>

      {/* Capabilities Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
        {capabilities.map((capability, index) => (
          <motion.div
            key={capability.title}
            variants={staggerItem}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            transition={{ delay: capability.delay }}
          >
            <CapabilityCard capability={capability} colors={colors} />
          </motion.div>
        ))}
      </div>

      {/* CTA Section */}
      <motion.div
        className="text-center"
        variants={fadeInUp}
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        transition={{ delay: 0.6 }}
      >
        <Card glass={true} className="inline-block p-8">
          <h3 className={`text-xl font-semibold ${colors.text.primary} mb-4`}>
            Ready to discuss your steel requirements?
          </h3>
          <p className={`${colors.text.secondary} mb-6 max-w-md`}>
            Our technical team can help you select the right products and 
            specifications for your project.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              variant="primary" 
              magnetic={true}
              onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Request Technical Consultation
            </Button>
            <Button 
              variant="ghost"
              onClick={() => document.querySelector('#products')?.scrollIntoView({ behavior: 'smooth' })}
            >
              View Product Specifications
            </Button>
          </div>
        </Card>
      </motion.div>
    </Section>
  );
};

const CapabilityCard = ({ capability, colors }) => {
  return (
    <motion.div
      variants={cardVariants}
      initial="rest"
      whileHover="hover"
      className="h-full perspective-1000"
    >
      <Card 
        gradient={true} 
        className="p-8 h-full cursor-pointer transition-all duration-300 group"
      >
        {/* Icon */}
        <motion.div 
          className="text-primary-400 mb-6 group-hover:scale-110 transition-transform duration-300"
          whileHover={{ rotate: 5 }}
        >
          {capability.icon}
        </motion.div>

        {/* Title */}
        <h3 className={`text-xl font-semibold ${colors.text.primary} mb-4 group-hover:text-primary-400 transition-colors duration-300`}>
          {capability.title}
        </h3>

        {/* Description */}
        <p className={`${colors.text.secondary} text-sm mb-6 leading-relaxed`}>
          {capability.description}
        </p>

        {/* Features */}
        <div className="space-y-2">
          {capability.features.map((feature, index) => (
            <motion.div
              key={feature}
              className={`flex items-center text-sm ${colors.text.muted}`}
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="w-1.5 h-1.5 bg-primary-400 rounded-full mr-3 flex-shrink-0"></div>
              {feature}
            </motion.div>
          ))}
        </div>

        {/* Learn More Arrow */}
        <motion.div
          className="mt-6 flex items-center text-primary-400 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          whileHover={{ x: 5 }}
        >
          <span>Learn More</span>
          <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </motion.div>
      </Card>
    </motion.div>
  );
};

export default Capabilities;